import { useState } from 'react'
import type { AnswerMap, AnswerValue, PhaseInfo, WorkflowStage } from '../../types/workflow'

interface ExperienceTemplateProps {
  stage: WorkflowStage
  answers: AnswerMap
  currentPhase?: PhaseInfo | null
  onSelect: (value: AnswerValue) => void
}

const STOP_REASONS = [
  { value: 'side_effects', label: 'Side effects' },
  { value: 'cost', label: 'Cost' },
  { value: 'not_effective', label: "Wasn't effective" },
  { value: 'doctor_advised', label: 'Doctor advised' },
  { value: 'other', label: 'Other' },
]

const TOLERABILITY = [
  { value: 'very_well', label: 'Very well' },
  { value: 'manageable', label: 'Manageable' },
  { value: 'significant_sides', label: 'Significant side effects' },
]

const EFFECTIVENESS = [
  { value: 'very_effective', label: 'Very effective' },
  { value: 'somewhat', label: 'Somewhat effective' },
  { value: 'not_effective', label: 'Not effective' },
]

export default function ExperienceTemplate({ answers, currentPhase, onSelect }: ExperienceTemplateProps) {
  const [whyStopped, setWhyStopped] = useState((answers?.whyStopped as string) || '')
  const [tolerability, setTolerability] = useState((answers?.tolerability as string) || '')
  const [effectiveness, setEffectiveness] = useState((answers?.effectiveness as string) || '')

  const canContinue = !!whyStopped && !!tolerability && !!effectiveness

  const handleContinue = () => {
    if (!canContinue) return
    onSelect({
      whyStopped,
      tolerability,
      effectiveness,
      experience_data: {
        whyStopped,
        tolerability,
        effectiveness,
      },
      glp1_discontinuation_reason: whyStopped,
      glp1_tolerance: tolerability,
      glp1_efficacy: effectiveness,
    })
  }

  return (
    <div className="flex-1 flex flex-col gap-4">
      <div className="flex flex-col gap-5">
        <fieldset className="flex flex-col gap-2 border-none">
          <legend className="mb-1.5 text-[13px] font-medium text-[var(--ww-text-2)]">Why did you stop?</legend>
          <div className="flex flex-wrap gap-1.5">
            {STOP_REASONS.map((reason) => (
              <button
                key={reason.value}
                type="button"
                className={`rounded-xl border px-4 py-3 text-[13px] font-medium transition-all duration-300 ${
                  whyStopped === reason.value
                    ? 'border-[var(--ww-accent)] choice-selected text-[var(--ww-text)] selected-glow'
                    : 'border-[var(--ww-glass-border)] bg-[var(--ww-surface)] text-[var(--ww-text-2)] hover:border-[var(--ww-border-hover)]'
                }`}
                onClick={() => setWhyStopped(reason.value)}
              >
                {reason.label}
              </button>
            ))}
          </div>
        </fieldset>

        <fieldset className="flex flex-col gap-2 border-none">
          <legend className="mb-1.5 text-[13px] font-medium text-[var(--ww-text-2)]">How well did you tolerate it?</legend>
          <div className="flex flex-wrap gap-1.5">
            {TOLERABILITY.map((item) => (
              <button
                key={item.value}
                type="button"
                className={`rounded-xl border px-4 py-3 text-[13px] font-medium transition-all duration-300 ${
                  tolerability === item.value
                    ? 'border-[var(--ww-accent)] choice-selected text-[var(--ww-text)] selected-glow'
                    : 'border-[var(--ww-glass-border)] bg-[var(--ww-surface)] text-[var(--ww-text-2)] hover:border-[var(--ww-border-hover)]'
                }`}
                onClick={() => setTolerability(item.value)}
              >
                {item.label}
              </button>
            ))}
          </div>
        </fieldset>

        <fieldset className="flex flex-col gap-2 border-none">
          <legend className="mb-1.5 text-[13px] font-medium text-[var(--ww-text-2)]">How effective was it?</legend>
          <div className="flex flex-wrap gap-1.5">
            {EFFECTIVENESS.map((item) => (
              <button
                key={item.value}
                type="button"
                className={`rounded-xl border px-4 py-3 text-[13px] font-medium transition-all duration-300 ${
                  effectiveness === item.value
                    ? 'border-[var(--ww-accent)] choice-selected text-[var(--ww-text)] selected-glow'
                    : 'border-[var(--ww-glass-border)] bg-[var(--ww-surface)] text-[var(--ww-text-2)] hover:border-[var(--ww-border-hover)]'
                }`}
                onClick={() => setEffectiveness(item.value)}
              >
                {item.label}
              </button>
            ))}
          </div>
        </fieldset>
      </div>

      <div className="mt-auto pt-6 pb-2 -ml-[42px] w-[calc(100%+42px)]">
        <button
          type="button"
          disabled={!canContinue}
          onClick={handleContinue}
          className="w-full py-4 px-6 text-[15px] font-semibold text-[var(--ww-btn-text)] bg-[var(--ww-btn-bg)] border-none rounded-full cursor-pointer transition-all duration-300 shadow-[0_2px_8px_rgba(0,0,0,0.1)] hover:not-disabled:bg-[var(--ww-btn-hover)] hover:not-disabled:-translate-y-0.5 disabled:opacity-25 disabled:cursor-default"
        >
          Continue
        </button>
      </div>
    </div>
  )
}
